import React from "react";
import ReactDOM from "react-dom/client";
import { MantineProvider, createTheme, rem } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";
import { ModalsProvider } from "@mantine/modals";
import App from "./App";
import "./components/styles/workspace.css";

// Shared workspace primitives. Feature state and calculations stay in their pages.
const theme = createTheme({
  primaryColor: "brand",
  primaryShade: 6,
  colors: {
    brand: ["#f4f3ff", "#eae8ff", "#d7d3ff", "#bcb5fc", "#9c93ef", "#8075e0", "#665bd1", "#564bb8", "#483f98", "#3b357a"],
  },
  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontFamilyMonospace: "'SFMono-Regular', Consolas, 'Liberation Mono', monospace",
  fontSizes: { xs: rem(11), sm: rem(12), md: rem(13), lg: rem(15), xl: rem(18) },
  lineHeights: { xs: "1.4", sm: "1.45", md: "1.5", lg: "1.55", xl: "1.6" },
  headings: { fontWeight: "600", sizes: {
    h1: { fontSize: rem(26), lineHeight: "1.3" },
    h2: { fontSize: rem(20), lineHeight: "1.35" },
    h3: { fontSize: rem(16), lineHeight: "1.4" },
    h4: { fontSize: rem(14), lineHeight: "1.4" },
  } },
  defaultRadius: "sm",
  radius: { xs: rem(3), sm: rem(5), md: rem(7), lg: rem(9), xl: rem(12) },
  shadows: { xs: "0 1px 2px #17171c05", sm: "0 2px 8px #17171c08", md: "0 8px 28px #17171c12", lg: "0 16px 48px #17171c1a", xl: "0 24px 64px #17171c20" },
  components: {
    Button: { defaultProps: { size: "sm", radius: "sm" }, styles: {
      root: { fontWeight: 500, fontSize: rem(12), height: rem(32), transition: "background-color 120ms ease", boxShadow: "0 1px 2px #17171c06" },
    } },
    ActionIcon: { defaultProps: { radius: "sm" } },
    Input: { defaultProps: { size: "sm", radius: "sm" }, styles: { input: { fontSize: rem(12), borderColor: "#e4e4e8", backgroundColor: "#ffffff" } } },
    InputWrapper: { styles: { label: { fontSize: rem(12), fontWeight: 500, marginBottom: rem(5), color: "#50505b" }, description: { fontSize: rem(11), color: "#85858f" } } },
    Text: { defaultProps: { size: "sm" } },
    Title: { styles: { root: { letterSpacing: "-0.025em", color: "#26262f" } } },
    Card: { defaultProps: { withBorder: true, shadow: "none", padding: "md" }, styles: { root: { borderColor: "#e7e7eb", backgroundColor: "#ffffff" } } },
    Paper: { styles: { root: { borderColor: "#e7e7eb" } } },
    Badge: { defaultProps: { radius: "sm", variant: "light" }, styles: { root: { fontWeight: 500, letterSpacing: 0, textTransform: "none" } } },
    Table: { defaultProps: { verticalSpacing: 9, horizontalSpacing: 12, highlightOnHover: true }, styles: {
      table: { color: "#464650", fontSize: rem(12), fontVariantNumeric: "tabular-nums" },
      thead: { backgroundColor: "#fafafb" }, th: { fontWeight: 500, color: "#7b7b85", fontSize: rem(11) },
      tr: { borderColor: "#ededf0" },
    } },
    Modal: { defaultProps: { radius: "lg", overlayProps: { backgroundOpacity: 0.22, blur: 2 } }, styles: { title: { fontWeight: 600, fontSize: rem(14) }, header: { borderBottom: "1px solid #ededf0", marginBottom: rem(16) }, body: { padding: rem(20) } } },
    Menu: { defaultProps: { radius: "md", shadow: "md" }, styles: { dropdown: { borderColor: "#e7e7eb", padding: rem(4) }, item: { fontSize: rem(12) } } },
    Tabs: { styles: { tab: { fontSize: rem(12), fontWeight: 500 }, list: { borderColor: "#e7e7eb" } } },
    SegmentedControl: { styles: { root: { backgroundColor: "#f1f1f4", padding: rem(3) }, label: { fontSize: rem(11), padding: "5px 9px" } } },
    Pagination: { defaultProps: { size: "sm", radius: "sm" } },
    Tooltip: { defaultProps: { radius: "sm" }, styles: { tooltip: { fontSize: rem(11) } } },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="light">
      <ModalsProvider>
        <Notifications />
        <App />
      </ModalsProvider>
    </MantineProvider>
  </React.StrictMode>,
);
